import searchContent from '@/lib/search-content.json';
import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  ArrowRight,
  Ruler,
  GitCompareArrows,
  Crop,
  Layers,
  Maximize2,
  Play,
} from "lucide-react";
import { useDocumentMeta } from "../lib/useDocumentMeta";
import { SEO_ROUTES } from "../lib/seo";
import { trackCtaClick } from "@/lib/analytics";
import ImageLightbox from "@/components/ImageLightbox";
import { DeliveryRows, Questions } from "@/components/Editorial";

/* =====================================================================
 * Plans page — Phase 4B real-capture replacement pass.
 * Real ForgedOps Plans operational captures replace all placeholder SVGs.
 * Restrained framing, drawing-first, no fake mockups.
 * Phase 4C-lightbox: every capture is click-to-expand via ImageLightbox.
 * ===================================================================== */

/* Image viewport frame — 3:2 to match the native capture aspect (1536x1024).
   Subtle border + premium shadow. Calm top-left operational badge.
   Now a clickable button that opens the page-level ImageLightbox. */
function CaptureFrame({
  src,
  alt,
  sheet,
  testid,
  priority = false,
  focal = "center",
  seqIndex,
  onExpand,
}) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={() => onExpand?.(seqIndex)}
      aria-label={`Expand ${alt || sheet}`}
      className="group relative w-full overflow-hidden border border-[#ff6b1a]/30 bg-[#06060a] shadow-[0_30px_80px_rgba(0,0,0,0.6)] cursor-zoom-in hover:border-[#ff6b1a]/60 transition-colors duration-300 block text-left"
      style={{ aspectRatio: "3 / 2" }}
    >
      <img
        src={src}
        alt={alt}
        loading={priority ? "eager" : "lazy"}
        decoding="async"
        draggable="false"
        className="absolute inset-0 w-full h-full object-cover select-none transition-transform [transition-duration:700ms] ease-out group-hover:scale-[1.015]"
        style={{ objectPosition: focal }}
      />
      {/* corner crosshair ticks — keep the operational viewport language */}
      <span className="absolute top-2 left-2 w-3 h-3 border-l border-t border-[#ff6b1a]/60 pointer-events-none" />
      <span className="absolute top-2 right-2 w-3 h-3 border-r border-t border-[#ff6b1a]/60 pointer-events-none" />
      <span className="absolute bottom-2 left-2 w-3 h-3 border-l border-b border-[#ff6b1a]/60 pointer-events-none" />
      <span className="absolute bottom-2 right-2 w-3 h-3 border-r border-b border-[#ff6b1a]/60 pointer-events-none" />
      {/* operational badge */}
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2 px-2.5 py-1 bg-black/75 border border-[#ff6b1a]/40 backdrop-blur-sm ml-4">
        <span className="w-1.5 h-1.5 bg-[#ff6b1a] rounded-full animate-pulse" />
        <span className="font-display uppercase tracking-[0.3em] text-[9px] md:text-[10px] text-[#ff6b1a] font-bold">
          Operational viewport · {sheet}
        </span>
      </div>
      {/* expand hint — bottom-right */}
      <div className="absolute bottom-3 right-3 z-10 flex items-center gap-2 px-2.5 py-1.5 bg-black/75 border border-zinc-700 backdrop-blur-sm opacity-80 group-hover:opacity-100 group-hover:border-[#ff6b1a]/50 transition-all duration-200 mr-4">
        <Maximize2 size={11} className="text-zinc-300 group-hover:text-[#ff6b1a] transition-colors" />
        <span className="font-display uppercase tracking-[0.28em] text-[9px] text-zinc-400 group-hover:text-white transition-colors font-bold">
          Expand
        </span>
      </div>
    </button>
  );
}

/* =====================================================================
 * Canonical walkthrough sequence — operational order, NOT layout order.
 * Each capture's CaptureFrame onClick opens the lightbox at its
 * walkthrough index so visitors can step through Plan Wall → Sheet Open →
 * Utility Coordination → Measure → Drainage → MOT → Revision → Markup → Field Mode.
 * ===================================================================== */
const WALKTHROUGH_SEQUENCE = [
  {
    src: "/media/plans/plans_drainage_sheet.jpg",
    alt: "ForgedOps Plans · Plan Wall · D-302 multi-panel drainage sheet · plan, section, elevation, grate, connection",
    caption: "01 · Plan Wall · D-302 Drainage Details",
  },
  {
    src: "/media/plans/plans_airfield.jpg",
    alt: "ForgedOps Plans · Sheet Open · C-104 RWY 17-35 Site & Grading · FAA airfield, structures, work limits",
    caption: "02 · Sheet Open · C-104 RWY 17-35",
  },
  {
    src: "/media/plans/plans_hero.jpg",
    alt: "ForgedOps Plans · Utility Coordination · C-103 Overall Site Plan · storm vs sanitary conflict, layered utilities",
    caption: "03 · Utility Coordination · C-103",
  },
  {
    src: "/media/plans/plans_measure.jpg",
    alt: "ForgedOps Plans · Measure Workflow · C-101 Roadway · linear 856.23 ft and area 2,457.83 SF measurements",
    caption: "04 · Measure Workflow · C-101",
  },
  {
    src: "/media/plans/plans_drainage.jpg",
    alt: "ForgedOps Plans · Drainage Detail · DS-04 Type C Inlet · invert in/out, rim, sump depth",
    caption: "05 · Drainage Detail · DS-04",
  },
  {
    src: "/media/plans/plans_mot.jpg",
    alt: "ForgedOps Plans · MOT Operations · MOT-07 Day Operation · tapers, signs, pedestrian route",
    caption: "06 · MOT Operations · MOT-07",
  },
  {
    src: "/media/plans/plans_revision.jpg",
    alt: "ForgedOps Plans · Revision Compare · SUPER-01 · REV A vs REV B · curb inlet added, headwall removed, ditch modified",
    caption: "07 · Revision Compare · SUPER-01",
  },
  {
    src: "/media/plans/plans_markup.jpg",
    alt: "ForgedOps Plans · Crop & Markup · D-301 Plan & Profile · verify invert elevation in field highlight",
    caption: "08 · Crop & Markup · D-301",
  },
  {
    src: "/media/plans/plans_field_mode.jpg",
    alt: "ForgedOps Plans · Field Mode · U-401 Utilities · layered utility crossings, Crossing C-17 clearance 1.57 ft",
    caption: "09 · Field Mode · U-401",
  },
];

const KEY_INTERACTIONS = [
  {
    icon: Ruler,
    title: "Measure Directly on Plans",
    body: "Linear distance, area, perimeter — captured on the sheet, with stationing and northing context preserved.",
  },
  {
    icon: GitCompareArrows,
    title: "Revision Compare for the Field",
    body: "Added / removed / modified — laid over the live sheet so supers see the change before they leave the trailer.",
  },
  {
    icon: Crop,
    title: "Markup and Field Highlight",
    body: "Highlight inverts, callouts, and verifications directly on the sheet — sent to the crew without leaving Plans.",
  },
  {
    icon: Layers,
    title: "Plan-Room Navigation",
    body: "Sheets ordered the way heavy civil reads them — civil, drainage, utilities, MOT, airfield.",
  },
];

const FIELD_MOMENTS = [
  {
    code: "01",
    label: "Revision Review · Superintendent",
    body:
      "REV A vs REV B compared on the live sheet. Added curb inlet, modified ditch bottom, removed headwall — surfaced before mobilization, not after.",
    src: "/media/plans/plans_revision.jpg",
    alt: "ForgedOps Plans · Revision Review · Super · DS-15 added, headwall removed, ditch bottom modified",
    sheet: "SUPER-01",
    seqIndex: 6,
  },
  {
    code: "02",
    label: "Drainage Detail · DS-04 Inlet",
    body:
      "Type C inlet detail with rim, invert in/out, sump depth, frame & grate spec — all readable in the cab, no panning around a PDF.",
    src: "/media/plans/plans_drainage.jpg",
    alt: "ForgedOps Plans · Drainage Detail · DS-04 Type C Inlet · 18 inch RCP in, 24 inch RCP out, rim 412.50",
    sheet: "D-302",
    seqIndex: 4,
  },
  {
    code: "03",
    label: "MOT · Day Operation",
    body:
      "60m tapers, 30m works area, single-file traffic, pedestrian route maintained, bus stop suspension flagged — laid out before the first cone goes down.",
    src: "/media/plans/plans_mot.jpg",
    alt: "ForgedOps Plans · MOT Day Operation · High Street drainage repairs · tapers, signs, pedestrian route",
    sheet: "MOT-07",
    seqIndex: 5,
  },
];

const PLAN_ROOM_TILES = [
  {
    src: "/media/plans/plans_airfield.jpg",
    alt: "ForgedOps Plans · FAA airfield · RWY 17-35 Site & Grading · taxiway, storm manhole, work limits",
    sheet: "C-104 · RWY 17-35",
    label: "FAA Airfield · Site & Grading",
    body:
      "Live airfield operations — RSA boundaries, storm manhole structure data, pavement removal limits, edge lights, PAPI references.",
    seqIndex: 1,
  },
  {
    src: "/media/plans/plans_drainage_sheet.jpg",
    alt: "ForgedOps Plans · Drainage Details D-302 · plan, section, elevation, grate, pipe connection",
    sheet: "D-302 · DRAINAGE DETAILS",
    label: "Multi-Panel Drainage Sheet",
    body:
      "Plan, section, elevation, grate, and pipe connection details — measured on-sheet without leaving the drawing.",
    seqIndex: 0,
  },
  {
    src: "/media/plans/plans_markup.jpg",
    alt: "ForgedOps Plans · Drainage Plan & Profile · highlight markup VERIFY INVERT ELEVATION IN FIELD",
    sheet: "D-301 · PLAN & PROFILE",
    label: "Field Markup · Verify Invert",
    body:
      "Highlight a structure, write the field note, route it to the crew — markups live with the sheet and survive the next revision.",
    seqIndex: 7,
  },
  {
    src: "/media/plans/plans_measure.jpg",
    alt: "ForgedOps Plans · Roadway sheet C-101 · linear 856.23 ft and area 2,457.83 SF measurements",
    sheet: "C-101 · ROADWAY",
    label: "Measure · Linear + Area",
    body:
      "856.23' alignment measure and a 2,457.83 SF takeoff area — captured directly on the sheet with full station context.",
    seqIndex: 3,
  },
];

const REALISM_POINTS = [
  "Built around real heavy-civil plan sets — not generic CAD templates.",
  "Validated on airport, DOT, utility, grading, and MOT drawings.",
  "Designed for real field use — readable in the cab and on the dirt.",
  "Optimized for tablets, foremen, and operations leaders — not desk analysts.",
];

export default function Plans() {
  useDocumentMeta(SEO_ROUTES["/plans"]);
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const openWalkthrough = (idx, source) => {
    trackCtaClick(
      "Plans Walkthrough Open",
      `plans-page · ${source || `step-${idx + 1}`}`,
    );
    setLightboxIdx(idx);
  };

  return (
    <div data-testid="plans-page" className="w-full bg-black">

      <ImageLightbox
        open={lightboxIdx !== null}
        onClose={() => setLightboxIdx(null)}
        items={WALKTHROUGH_SEQUENCE}
        index={lightboxIdx ?? 0}
        onIndexChange={setLightboxIdx}
      />
      {/* ============ 1 · HERO ============ */}
      <section
        data-testid="plans-hero"
        className="plans-expanded-hero relative pt-16 pb-20 md:pt-20 md:pb-24 overflow-hidden border-b border-zinc-900"
      >
        <div className="absolute -top-32 -left-32 w-[700px] h-[700px] rounded-full bg-[radial-gradient(circle,rgba(255,107,26,0.16)_0%,transparent_70%)] blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col gap-10 lg:gap-14 items-stretch">
          {/* Copy column */}
          <div className="plans-expanded-copy flex flex-col gap-7">
            <div
              data-testid="plans-status-chip"
              className="inline-flex items-center gap-2.5 px-4 py-2 border border-[#ff6b1a]/40 bg-[#ff6b1a]/10 w-fit"
            >
              <span className="w-1.5 h-1.5 bg-[#ff6b1a] rounded-full animate-pulse" />
              <span className="font-display uppercase tracking-[0.3em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
                Available Now · ForgedOps Plans
              </span>
            </div>

            <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-zinc-500 font-bold">
              ForgedOps Plans
            </span>

            <h1
              data-testid="plans-headline"
              className="font-display uppercase font-black tracking-[-0.025em] leading-[0.88] text-[clamp(2.6rem,6.5vw,6rem)] text-balance metallic-text"
            >
              The Plan Room.
              <br />
              Built Like the Field.
            </h1>

            <p className="text-zinc-300 text-base md:text-lg leading-relaxed max-w-3xl">
              Layered utilities. Drawing coordination. Measurements on
              real drawings. ForgedOps Plans brings field, office, and subs onto
              a single intelligent plan set — so heavy civil crews build with
              confidence.
            </p>

            <div className="flex flex-wrap items-center gap-3 mt-2">
              <a
                href={searchContent.plansUrl}
                data-testid="plans-primary-cta"
                onClick={() => trackCtaClick("Visit Plans & Buy Online", "plans-hero")}
                className="group inline-flex items-center gap-3 px-7 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-[#ff8533] transition-all shadow-[0_0_40px_rgba(255,107,26,0.35)] hover:shadow-[0_0_60px_rgba(255,107,26,0.55)]"
              >
                Visit Plans & Buy Online
                <ArrowUpRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <Link className="forge-button forge-button-secondary" to="/contact?interest=ForgedOps%20Plans">Request Custom Pricing</Link>
              <button
                type="button"
                data-testid="plans-walkthrough-cta"
                onClick={() => openWalkthrough(0, "hero-walk-cta")}
                className="group inline-flex items-center gap-3 px-6 py-4 border border-zinc-700 bg-black/40 text-white font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:border-[#ff6b1a]/70 hover:bg-black/60 transition-colors duration-200"
                aria-label="Walk the plan room — open the 9-step operational walkthrough"
              >
                <Play
                  size={12}
                  strokeWidth={1.8}
                  className="text-[#ff6b1a] transition-transform duration-200 group-hover:translate-x-0.5"
                />
                <span>Walk the plan room</span>
                <span
                  data-testid="plans-walkthrough-step-indicator"
                  className="ml-1 pl-3 border-l border-zinc-700 text-zinc-500 tracking-[0.28em] text-[10px] font-bold group-hover:text-zinc-300 transition-colors"
                >
                  01 / 09
                </span>
              </button>
            </div>
          </div>

          {/* Hero capture */}
          <div className="plans-expanded-visual">
            <CaptureFrame
              testid="plans-hero-viewport"
              src="/media/plans/plans_hero.jpg"
              alt="ForgedOps Plans · Overall Site Plan C-103 · layered utility coordination, storm vs sanitary conflict, measurements, markups"
              sheet="C-103"
              seqIndex={2}
              onExpand={(i) => openWalkthrough(i, "hero")}
              priority
            />
          </div>
        </div>
      </section>

      {/* ============ 2 · OPERATIONAL IMMERSION ============ */}
      <section
        data-testid="plans-immersion"
        className="relative bg-[#050507] border-b border-zinc-900 py-24 md:py-32"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col gap-10">
          <div className="flex flex-col gap-4 max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="block w-10 h-[2px] bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
                Operational Immersion
              </span>
            </div>
            <h2 className="font-display uppercase font-black leading-[0.95] tracking-[-0.02em] text-[clamp(1.7rem,3.8vw,3.2rem)] text-balance">
              Field Mode. Utility Crossings. Live Coordination.
            </h2>
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
              Layered utilities — gas, electric, water, storm, sanitary — drawn
              live on the sheet with crossing intelligence and clearance data.
              Bring the drawing context into the field conversation. The plan room walks onto the
              jobsite.
            </p>
          </div>

          <CaptureFrame
            testid="plans-immersion-viewport"
            src="/media/plans/plans_field_mode.jpg"
            alt="ForgedOps Plans · Field Mode · U-401 Utilities · GAS / ELECTRIC / WATER / STORM / SANITARY layers, Crossing C-17 with 1.57 ft clearance"
            sheet="U-401 · Field Mode"
            seqIndex={8}
            onExpand={(i) => openWalkthrough(i, "immersion")}
          />
        </div>
      </section>

      {/* ============ 3 · KEY INTERACTIONS ============ */}
      <section
        data-testid="plans-interactions"
        className="bg-black border-b border-zinc-900 py-24 md:py-32"
      >
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col items-start gap-4 mb-12 md:mb-14 max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="block w-10 h-[2px] bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
                Key Interactions
              </span>
            </div>
            <h2 className="font-display uppercase font-black leading-[0.95] tracking-[-0.02em] text-[clamp(1.6rem,3.5vw,2.8rem)] text-balance">
              Review. Measure. Mark Up. Coordinate.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
            {KEY_INTERACTIONS.map((k) => {
              const Icon = k.icon;
              return (
                <div
                  key={k.title}
                  className="bg-[#06060a] p-7 md:p-8 flex flex-col gap-4 min-h-[200px]"
                  data-testid={`plans-interaction-${k.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
                >
                  <Icon className="text-[#ff6b1a]" size={20} strokeWidth={1.5} />
                  <h3 className="font-display uppercase font-black text-base md:text-lg tracking-tight leading-tight">
                    {k.title}
                  </h3>
                  <p className="text-zinc-400 text-[13px] leading-relaxed">{k.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ============ 4 · FIELD WORKFLOW MOMENTS ============ */}
      <section
        data-testid="plans-moments"
        className="bg-[#050507] border-b border-zinc-900 py-24 md:py-32"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col gap-14 md:gap-20">
          <div className="flex flex-col items-start gap-4 max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="block w-10 h-[2px] bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
                Field Workflow Moments
              </span>
            </div>
            <h2 className="font-display uppercase font-black leading-[0.95] tracking-[-0.02em] text-[clamp(1.7rem,3.8vw,3rem)] text-balance">
              Real Sheets. Real Revisions. Real Operations.
            </h2>
          </div>

          {FIELD_MOMENTS.map((m, i) => (
            <div
              key={m.code}
              data-testid={`plans-moment-${m.code}`}
              className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center ${
                i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
              }`}
            >
              <div className="lg:col-span-5 flex flex-col gap-4">
                <span className="font-display uppercase tracking-[0.45em] text-[10px] text-[#ff6b1a] font-bold">
                  Moment · {m.code}
                </span>
                <h3 className="font-display uppercase font-black text-2xl md:text-3xl tracking-tight leading-tight">
                  {m.label}
                </h3>
                <p className="text-zinc-400 text-sm md:text-base leading-relaxed max-w-md">
                  {m.body}
                </p>
              </div>
              <div className="lg:col-span-7">
                <CaptureFrame
                  testid={`plans-moment-${m.code}-viewport`}
                  src={m.src}
                  alt={m.alt}
                  sheet={m.sheet}
                  seqIndex={m.seqIndex}
                  onExpand={(i) => openWalkthrough(i, `moment-${m.code}`)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 5 · PLAN ROOM · OPERATIONAL SHEETS ============ */}
      <section
        data-testid="plans-plan-room"
        className="bg-black border-b border-zinc-900 py-24 md:py-32"
      >
        <div className="max-w-[1500px] mx-auto px-6 lg:px-10 flex flex-col gap-12 md:gap-14">
          <div className="flex flex-col items-start gap-4 max-w-3xl">
            <div className="flex items-center gap-3">
              <span className="block w-10 h-[2px] bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
                The Plan Room
              </span>
            </div>
            <h2 className="font-display uppercase font-black leading-[0.95] tracking-[-0.02em] text-[clamp(1.7rem,3.8vw,3rem)] text-balance">
              Airfield. Drainage. Markup. Measure.
            </h2>
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
              Explore four drawing workflows in ForgedOps Plans —
              every sheet read the way heavy civil actually reads it.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8">
            {PLAN_ROOM_TILES.map((t, i) => (
              <div
                key={t.sheet}
                data-testid={`plans-room-tile-${i}`}
                className="flex flex-col gap-4"
              >
                <CaptureFrame
                  testid={`plans-room-viewport-${i}`}
                  src={t.src}
                  alt={t.alt}
                  sheet={t.sheet}
                  seqIndex={t.seqIndex}
                  onExpand={(idx) => openWalkthrough(idx, `room-${t.sheet}`)}
                />
                <div className="flex flex-col gap-2 px-1">
                  <span className="font-display uppercase tracking-[0.32em] text-[10px] text-[#ff6b1a] font-bold">
                    {t.sheet}
                  </span>
                  <h3 className="font-display uppercase font-black text-lg md:text-xl tracking-tight leading-tight">
                    {t.label}
                  </h3>
                  <p className="text-zinc-400 text-sm leading-relaxed">{t.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ 6 · REAL-WORLD REALISM ============ */}
      <section
        data-testid="plans-realism"
        className="relative bg-[#050507] border-b border-zinc-900 py-24 md:py-32 overflow-hidden"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.05] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,107,26,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,107,26,0.5) 1px, transparent 1px)",
            backgroundSize: "70px 70px",
          }}
        />
        <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 flex flex-col gap-5">
            <div className="flex items-center gap-3">
              <span className="block w-10 h-[2px] bg-[#ff6b1a]" />
              <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
                Real-World Realism
              </span>
            </div>
            <h2 className="font-display uppercase font-black leading-[0.95] tracking-[-0.02em] text-[clamp(1.6rem,3.4vw,2.8rem)] text-balance">
              Built for the Way Heavy Civil Actually Runs.
            </h2>
            <p className="text-zinc-400 text-base leading-relaxed">
              Plans brings drawing review, measurement, markup, and revision comparison into a workflow built around the way construction teams use their plans.
            </p>
          </div>

          <ul className="lg:col-span-7 flex flex-col">
            {REALISM_POINTS.map((p, i) => (
              <li
                key={p}
                data-testid={`plans-realism-${i}`}
                className="flex items-start gap-5 py-5 border-b border-zinc-900 last:border-b-0"
              >
                <span className="font-display uppercase tracking-[0.4em] text-[10px] text-[#ff6b1a] font-bold pt-1">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="text-zinc-200 text-base md:text-[17px] leading-relaxed">
                  {p}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <div className="forge-site"><DeliveryRows eyebrow="How the Plan Room Supports the Work" title="From Drawing Review to a Field Decision." rows={[{title:"Understand the sheet",body:"Open the relevant drawing and review the details, notes, and layered context. Keep the plan at the center of the conversation between project teams and field leadership.",output:"The sheet and information the team needs to review."},{title:"Measure & mark up",body:"Work directly on the drawing to review dimensions, measure areas, and mark the details that need attention. Keep the discussion anchored to a visible location on the plan.",output:"Measurements and markups tied to the drawing workflow."},{title:"Review the revision",body:"Compare changes before the team works from a revised sheet. Use the revision workflow to support the conversation about what changed and what needs a field review.",output:"A clearer review of additions, removals, and modifications."}]} /><Questions items={searchContent.faqs["/plans"]}/></div>
      {/* ============ 7 · CLOSING CTA ============ */}
      <section
        data-testid="plans-cta"
        className="relative bg-black py-24 md:py-32 border-b border-zinc-900 overflow-hidden"
      >
        <div className="absolute -bottom-32 -right-32 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(255,107,26,0.18)_0%,transparent_70%)] blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-[1100px] mx-auto px-6 lg:px-10 flex flex-col items-start gap-8">
          <span className="font-display uppercase tracking-[0.5em] text-[10px] md:text-[11px] text-[#ff6b1a] font-bold">
            Built in the field
          </span>
          <h2 className="font-display uppercase font-black leading-[0.92] tracking-[-0.025em] text-[clamp(2.2rem,5vw,4.4rem)] text-balance metallic-text max-w-4xl">
            Built in the Field.
            <br />
            Refined in Operations.
          </h2>
          <p className="text-zinc-400 text-base md:text-lg leading-relaxed max-w-2xl">
            ForgedOps Plans is available now at forgedopsplans.com. Review current plans and purchase through the platform, or contact ForgedOps for a custom deal and pricing based on your team and requirements.
          </p>
          <div className="flex flex-wrap items-center gap-4 mt-2">
            <a
              href={searchContent.plansUrl}
              data-testid="plans-footer-cta"
              onClick={() => trackCtaClick("Visit Plans & Buy Online", "plans-footer")}
              className="group inline-flex items-center gap-3 px-7 py-4 bg-[#ff6b1a] text-black font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-[#ff8533] transition-all shadow-[0_0_40px_rgba(255,107,26,0.35)] hover:shadow-[0_0_60px_rgba(255,107,26,0.55)]"
            >
              Visit Plans & Buy Online
              <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
            </a>
            <Link
              to="/contact?interest=ForgedOps%20Plans"
              data-testid="plans-footer-secondary-cta"
              className="inline-flex items-center gap-3 px-7 py-4 border border-zinc-600 text-white font-display uppercase tracking-[0.18em] text-[12px] font-bold hover:bg-white/5 hover:border-[#ff6b1a] transition-all"
            >
              Request Custom Pricing
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}


